import {
    mdiChevronRight,
    mdiChevronLeft,
    mdiMagnify,
    mdiExpandAll,
    mdiCollapseAll,
    mdiClose,
} from '@mdi/js';
import Icon from '@mdi/react';
import { Button, IconButton, InputAdornment, Stack, SxProps, TextField } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { headingFontBold, vars } from '../../../Theme';

/**
 * Gets the styles for the Sidebar.
 * @param isOpen - Defines if Sidebar is opened.
 */
const getSidebarSx = (isOpen: boolean): SxProps => ({
    position: 'relative',
    height: '100%',
    flex: 0,
    width: isOpen ? '100%' : 40,
    maxWidth: isOpen ? 'none' : 40,
    flexGrow: 4,
    py: 2.5,
    pl: 1.5,
    pr: 3,
});

/**
 * Styles for the Search field.
 */
const searchSx: SxProps = {
    '.MuiInputBase-root': {
        height: 36,
        p: 0,
    },
    '.MuiInputBase-input': {
        py: 0,
    },
};

/**
 * Styles for the actions IconButton.
 */
const actionsIconBtnSx: SxProps = {
    width: 36,
    height: 36,
    color: vars.black,
    p: 1,
};

/**
 * Styles for the toggle button.
 */
const toggleBtnSx: SxProps = {
    backgroundColor: vars.fillGray,
    position: 'absolute',
    top: '22px',
    right: '-16px',
    zIndex: 1210,
    '&:hover': {
        backgroundColor: vars.lightGray,
    },
};
/**
 * Gets the styles for the "all data" button.
 * @param active - Button active state.
 */
const getBtnSx = (active: boolean): SxProps => ({
    bgcolor: active ? vars.bgGreen : 'transparent',
    color: active ? vars.brandGreen : vars.black,
    justifyContent: 'left',
    textTransform: 'none',
    ...headingFontBold,
    fontSize: 16,
    flexGrow: 4,
    height: 44,
});

/**
 * Customisable Application Sidebar.
 */
export default (props: ISidebarProps) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'sidebar' });

    /** Deconstruct props. */
    const { openState, tree, handleToggleAllExpanded, handleSearchChange, searchQuery, mainBtn } =
        props;
    const [isOpen, setIsOpen] = openState;

    const sidebarActionsPanelConfig = [
        ...(handleToggleAllExpanded
            ? [
                  {
                      icon: mdiExpandAll,
                      type: 'icon',
                      title: 'expand_all',
                      callBack: () => handleToggleAllExpanded(true),
                  },
              ]
            : []),
        ...(handleToggleAllExpanded
            ? [
                  {
                      icon: mdiCollapseAll,
                      type: 'icon',
                      title: 'collapse_all',
                      callBack: () => handleToggleAllExpanded(false),
                  },
              ]
            : []),
        ...(mainBtn
            ? [
                  {
                      type: 'button',
                      title: mainBtn.title,
                      active: mainBtn.active,
                      callBack: mainBtn.onClick,
                  },
              ]
            : []),
    ];

    /**
     * Toggles the open state of the sidebar.
     */
    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    /**
     * Creates the sidebar's toggle button.
     */
    const toggleBtn = () => (
        <IconButton
            sx={toggleBtnSx}
            onClick={handleToggle}
            title={t(isOpen ? 'collapse' : 'expand')}
            size='small'
        >
            <Icon path={isOpen ? mdiChevronLeft : mdiChevronRight} size={1} />
        </IconButton>
    );

    /** Button that allows the user to reset the view to the 'all data' option. */
    const sidebarActionsPanel = (
        <Stack
            direction='row'
            sx={{
                justifyContent: 'flex-end',
                pb: 2,
            }}
        >
            {handleSearchChange ? (
                <TextField
                    id='category-filter'
                    placeholder={t('search')}
                    value={searchQuery}
                    onChange={e => handleSearchChange(e.target.value)}
                    sx={searchSx}
                    fullWidth
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position='start' sx={actionsIconBtnSx}>
                                    <Icon path={mdiMagnify} size={1} />
                                </InputAdornment>
                            ),
                            endAdornment: searchQuery && (
                                <IconButton
                                    aria-label='toggle password visibility'
                                    onClick={() => handleSearchChange('')}
                                    sx={{
                                        ...actionsIconBtnSx,
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                >
                                    <Icon path={mdiClose} size={1} />
                                </IconButton>
                            ),
                        },
                    }}
                />
            ) : null}
            {sidebarActionsPanelConfig.map(btn => {
                return btn.type === 'icon' ? (
                    <IconButton
                        key={btn.title}
                        sx={actionsIconBtnSx}
                        title={btn.title}
                        onClick={btn.callBack}
                    >
                        <Icon path={btn.icon} size={1} />
                    </IconButton>
                ) : (
                    <Button key={btn.title} sx={getBtnSx(btn.active)} onClick={btn.callBack}>
                        {btn.title}
                    </Button>
                );
            })}
        </Stack>
    );

    return (
        <Stack direction='column' sx={{ ...getSidebarSx(isOpen) }}>
            <Stack>
                {isOpen && sidebarActionsPanel}
                {toggleBtn()}
            </Stack>

            {isOpen && tree}
        </Stack>
    );
};

/**
 * Interface that describes the props for the Sidebar component.
 */
export interface ISidebarProps {
    /** State slice for controlling the sidebar's open state. */
    openState: [boolean, (boolean) => void];

    /** TreeView component to render within the sidebar. */
    tree: JSX.Element;

    mainBtn?: ISidebarAllDataBtnProps;

    searchQuery?: string;

    handleToggleAllExpanded?: (value: boolean) => void;

    handleSearchChange?: (value: string) => void;
}

/**
 * Interface that describes the props for the Sidebar's 'all data' button.
 */
export interface ISidebarAllDataBtnProps {
    /** If the button should be rendered as active or not. */
    active: boolean;

    /** Button text. */
    title: string;

    /** Button onClick handler. */
    onClick: () => void;
}
